import { useEffect, useState } from 'react';
import '../Bonus.scss';
import {
  Card,
  CardBody,
  Table,
  TabContent,
  TabPane,
  Col,
  UncontrolledTooltip,
} from 'reactstrap';
import Icon from '@mdi/react';
import { mdiInformationOutline } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import { fetchBonusDetails } from '../../../store/apis/bonusService';
import { quarters } from '../constants';
import { getCurrentQuarter } from '../../../utils/date.util';

function BonusDetails({ formData }) {
  const currentQuarter = getCurrentQuarter();
  const [activeTab, setActiveTab] = useState('Q1');

  useEffect(() => {
    setActiveTab(`Q${currentQuarter}`);
  }, [currentQuarter]);

  const {
    data: fetchedDetails,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    refetch: refetchDetails,
  } = useQuery({
    queryKey: ['fetchedDetails', formData],
    queryFn: () => fetchBonusDetails({ type: 'DETAIL', ...formData }),
    enabled: !!formData?.empCode,
  });

  useEffect(() => {
    if (formData?.empCode && formData?.period) {
      refetchDetails();
    }
  }, [formData.empCode, refetchDetails, formData?.period]);

  let detailsForQuarter = Array.isArray(fetchedDetails)
    ? fetchedDetails.filter(item => `Q${item.Qtr}` === activeTab)
    : [];

  const noDetailsAvailable =
    !isDetailsLoading &&
    !isDetailsError &&
    (!fetchedDetails || fetchedDetails?.length === 0);

  // Calculate totals
  const totalQuarterlyPayout = detailsForQuarter?.reduce(
    (sum, item) => sum + (parseFloat(item?.Bonus) || 0),
    0,
  );
  const totalCumulativePayout = detailsForQuarter?.reduce(
    (sum, item) => sum + (parseFloat(item?.Cumulative_Bonus) || 0),
    0,
  );

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <Card className="quarterly-card">
      <CardBody className="p-0">
        <div className="d-flex flex-row justify-content-between align-items-center bonus-tab-header">
          <Col lg={9.5}>
            <ul className="nav nav-underline">
              <button
                type="button"
                onClick={() => toggleTab('Q1')}
                className={`cursor-pointer nav-link px-3 pt-2 ${activeTab === 'Q1' && 'active'}`}
              >
                Quarter 1
              </button>
              <button
                type="button"
                onClick={() => toggleTab('Q2')}
                className={`cursor-pointer nav-link px-3 pt-2 ${activeTab === 'Q2' && 'active'}`}
              >
                Quarter 2
              </button>
              <button
                type="button"
                onClick={() => toggleTab('Q3')}
                className={`cursor-pointer nav-link px-3 pt-2 ${activeTab === 'Q3' && 'active'}`}
              >
                Quarter 3
              </button>
              <button
                type="button"
                onClick={() => toggleTab('Q4')}
                className={`cursor-pointer nav-link px-3 pt-2 ${activeTab === 'Q4' && 'active'}`}
              >
                Quarter 4
              </button>
            </ul>
          </Col>
        </div>
        <div className="quarterly-tabs-separator" />
        <TabContent activeTab={activeTab}>
          {quarters.map((quarter, index) => {
            detailsForQuarter = Array.isArray(fetchedDetails)
              ? fetchedDetails.filter(item => `Q${item.Qtr}` === quarter)
              : [];

            return (
              <TabPane key={index} tabId={quarter}>
                <Card className="m-2">
                  <CardBody className="p-0 quarter-active-card">
                    <div className="table-responsive" style={{ width: '100%' }}>
                      <Table
                        bordered
                        className="m-0 text-center align-middle quarter-active-table w-100"
                      >
                        <thead>
                          <tr>
                            <th
                              colSpan="8"
                              className="text-start px-3 py-1 quarter-active-table-separator"
                            >
                              <div className="quarter-column">
                                <span className="me-2 bonus-detail-title">
                                  Current Quarter {quarter}
                                </span>
                                <span>(all values are in $) </span>
                                <span
                                  id={`current-quarterly-info-icon-${quarter}`}
                                >
                                  <Icon
                                    path={mdiInformationOutline}
                                    size={0.7}
                                    style={{ cursor: 'pointer' }}
                                    color="#065a86"
                                  />
                                </span>
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`current-quarterly-info-icon-${quarter}`}
                                >
                                  Current Quarter {quarter} - Shows quarterly
                                  performance metrics and bonus calculations.
                                  All monetary values are displayed in dollars
                                  ($).
                                </UncontrolledTooltip>
                              </div>
                            </th>
                            <th
                              colSpan="5"
                              className="text-start px-3 py-1 quarter-column"
                            >
                              <span className="me-2 bonus-detail-title">
                                Cumulative
                              </span>
                              <span id={`cumulative-info-icon-${quarter}`}>
                                <Icon
                                  path={mdiInformationOutline}
                                  size={0.6}
                                  style={{ cursor: 'pointer' }}
                                  color="#065a86"
                                />
                              </span>
                              <UncontrolledTooltip
                                placement="top"
                                target={`cumulative-info-icon-${quarter}`}
                              >
                                Cumulative - Shows year-to-date performance
                                metrics including all quarters up to {quarter}.
                                Values represent cumulative bonus calculations.
                              </UncontrolledTooltip>
                            </th>
                          </tr>
                          <tr className="quarterly-table-header">
                            <td className="py-1 criteria-column">Criteria</td>
                            <td
                              className="py-1 quarter-column"
                              title="KPI section weight as per scheme"
                            >
                              Weight
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Numerical KPI targets of Quarter"
                            >
                              Target
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Numerical KPI results of Quarter"
                            >
                              Result
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Carry forward from previous quarter results over 120% (from Q2 onwards)"
                            >
                              Carry Fwd.
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="% of achievement over target"
                            >
                              Result%
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="As per Numerical curve - vs. Result%"
                            >
                              Score
                            </td>
                            <td
                              className="py-1 quarter-active-table-separator quarter-column"
                              title="Actual no. of months basic salary bonus for quarterly portion as per the result and score"
                            >
                              Act. Payout
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Numerical KPI targets for cumulative period (including past quarters in FY)"
                            >
                              Target
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Numerical KPI results for cumulative period (including past quarters in FY)"
                            >
                              Result
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="% of achievement over target"
                            >
                              Result%
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="As per Numerical curve vs. Result%"
                            >
                              Score
                            </td>
                            <td
                              className="py-1 quarter-column"
                              title="Actual no. of months basic salary bonus for cumulative period as per the result and score"
                            >
                              Cum. Payout
                            </td>
                          </tr>
                        </thead>
                        <tbody>
                          {isDetailsLoading && (
                            <tr>
                              <td colSpan={13}>Loading...</td>
                            </tr>
                          )}
                          {isDetailsError && (
                            <tr>
                              <td colSpan={13}>Error loading details</td>
                            </tr>
                          )}
                          {noDetailsAvailable && (
                            <tr>
                              <td colSpan={13} className="text-danger fw-bold">
                                No Bonus data available
                              </td>
                            </tr>
                          )}
                          {!isDetailsLoading &&
                            !isDetailsError &&
                            detailsForQuarter?.length > 0 &&
                            detailsForQuarter.map((item, dindex) => (
                              <tr key={dindex} className="table-row-tall">
                                <td
                                  style={{ color: '#026AA2' }}
                                  className="py-1 fw-semibold criteria-column"
                                >
                                  {item?.KPI}
                                </td>
                                <td className="py-1 quarter-column">
                                  {item?.Weight}
                                </td>
                                <td className="py-1 quarter-column">
                                  {`${parseInt(item?.Target, 10).toLocaleString()}`}
                                </td>
                                <td className="py-1 quarter-column">
                                  {`${parseInt(item?.Result, 10).toLocaleString()}`}
                                </td>
                                <td className="py-1 quarter-column">
                                  {`${parseInt(item?.CarryForward, 10).toLocaleString()}`}
                                </td>
                                <td className="py-1 quarter-column">{`${item?.ResultPerc}%`}</td>
                                <td className="py-1 quarter-column">
                                  {item?.Score}
                                </td>
                                <td className="py-1 quarter-payout-table-data quarter-border-right quarter-column">
                                  {/* {toFixedTwoDecimal(item?.Bonus)} */}
                                  {parseFloat(item?.Bonus).toLocaleString(
                                    undefined,
                                    {
                                      minimumFractionDigits: 3,
                                      maximumFractionDigits: 3,
                                    },
                                  )}
                                </td>
                                <td className="py-1 quarter-column">
                                  {/* {parseFloat(
                                    item?.Cumulative_Target,
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3,
                                  })} */}
                                  {`${parseInt(item?.Cumulative_Target, 10).toLocaleString()}`}
                                </td>
                                <td className="py-1 quarter-column">
                                  {/* {parseFloat(
                                    item?.Cumulative_Result,
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3,
                                  })} */}
                                  {`${parseInt(item?.Cumulative_Result, 10).toLocaleString()}`}
                                </td>
                                <td className="py-1 quarter-column">{`${item?.Cumulative_ResultPerc}%`}</td>
                                <td className="py-1 quarter-column">
                                  {item?.Cumulative_Score}
                                </td>
                                <td className="py-1 quarter-payout-table-data quarter-column">
                                  {parseFloat(
                                    item?.Cumulative_Bonus,
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 3,
                                  })}
                                </td>
                              </tr>
                            ))}
                          <tr className="fw-semibold quarterly-total-payout-row">
                            <td colSpan="4" className="py-1">
                              {}
                            </td>
                            <td
                              colSpan="3"
                              className="payout-footer py-1 quarter-column"
                              title="No. of months basic salary actual payout in a quarter based on the results and score"
                            >
                              Quarterly Payout Result
                            </td>
                            <td className="py-1 payout-footer quarterly-total-payout-right quarter-column">
                              {/* {toFixedTwoDecimal(totalQuarterlyPayout)} */}
                              {parseFloat(totalQuarterlyPayout).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 3,
                                  maximumFractionDigits: 3,
                                },
                              )}
                            </td>
                            <td
                              colSpan="4"
                              className="payout-footer py-1 quarter-column"
                              title="No. of months basic salary actual payout in a quarter for cumulative period based on the results and score"
                            >
                              Cumulative Payout Result
                            </td>
                            <td className="py-1 payout-footer quarter-column">
                              {parseFloat(totalCumulativePayout).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 3,
                                  maximumFractionDigits: 3,
                                },
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </TabPane>
            );
          })}
        </TabContent>
      </CardBody>
    </Card>
  );
}

export default BonusDetails;
